import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import styled from 'styled-components';
import Web3 from 'web3';
import { compose } from 'recompose';
import { Pagination, Icon, message } from 'antd';
import Button from '@material-ui/core/Button';
import {
  getTokenContract,
  getVoteContract,
  methods
} from 'utilities/ContractService';
import { connectAccount } from 'core';
import Proposal from 'components/Basic/Proposal';
import ProposalModal from 'components/Vote/ProposalModal';
import LoadingSpinner from 'components/Basic/LoadingSpinner';
import { Card } from 'components/Basic/Card';
import { useInstance } from 'hooks/useContract';

const ProposalsWrapper = styled.div`
  width: 100%;
  min-height: 555px;
  border-radius: 6px;
  background-color: var(--color-bg-primary);
  padding: 20px 30px;

  .proposal-head {
    margin-bottom: 20px;
    .header {
      font-size: 17px;
      font-weight: 900;
      color: var(--color-text-main);
    }
    .create-proposal-btn {
      width: 210px;
      height: 40px;
      border-radius: 6px;
      background: linear-gradient(
        242deg,
        #246cf9 0%,
        #1e68f6 0.01%,
        #0047d0 100%,
        #0047d0 100%
      );
      .MuiButton-label {
        font-size: 17px;
        font-weight: 500;
        color: var(--color-white);
        text-transform: capitalize;
      }
    }
  }

  .body {
    height: 100%;
  }

  .footer {
    margin-top: 40px;
    .pages {
      font-size: 16px;
      color: var(--color-text-secondary);
    }

    @media (max-width: 768px) {
      flex-direction: column;
    }

    .ant-pagination-item {
      border: none;
    }

    .ant-pagination-next .ant-pagination-item-link {
      border: none;
    }

    .ant-pagination-prev .ant-pagination-item-link {
      border: none;
    }

    .ant-pagination-item-active {
      border: 1px solid #107def;
    }

    .button {
      width: 200px;
      flex-direction: row-reverse;

      span {
        font-size: 16px;
        font-weight: 900;
        color: var(--color-text-main);
      }

      img {
        width: 26px;
        height: 16px;
        border-radius: 50%;
      }

      .button-prev {
        cursor: pointer;
        img {
          margin-right: 25px;
          transform: rotate(180deg);
        }
      }

      .button-next {
        cursor: pointer;
        span {
          margin-right: 25px;
        }
      }

      .button-prev:focus,
      .button-prev:hover,
      .button-next:focus,
      .button-next:hover {
        span {
          color: var(--color-blue);
        }
      }

      @media (max-width: 768px) {
        gap: 10px;
        justify-content: center;

        img {
          width: 20px;
          height: 16px;
          border-radius: 50%;
        }

        .button-prev {
          cursor: pointer;
          img {
            margin-right: 12px;
            transform: rotate(180deg);
          }
        }

        .button-next {
          cursor: pointer;
          span {
            margin-right: 12px;
          }
        }
      }
    }
  }
`;

const NoProposalWrapper = styled.div`
  width: 100%;
  height: 100%;

  .title {
    font-size: 16px;
    font-weight: 900;
    color: var(--color-text-main);
  }
`;
function Proposals({
  address,
  isLoadingProposal,
  settings,
  votingWeight,
  pageNumber,
  proposals,
  total,
  onChangePage,
  intl
}) {
  const instance = useInstance(settings.walletConnected);

  const [current, setCurrent] = useState(pageNumber);
  const [pageSize, setPageSize] = useState(5);

  const [proposalModal, setProposalModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [proposalThreshold, setProposalThreshold] = useState(0);
  const [maxOperation, setMaxOperation] = useState(0);
  const [delegateAddress, setDelegateAddress] = useState('');

  useEffect(() => {
    if (address) {
      const voteContract = getVoteContract(instance);
      methods.call(voteContract.methods.proposalThreshold, []).then(res => {
        setProposalThreshold(+Web3.utils.fromWei(res, 'ether'));
      });
      methods.call(voteContract.methods.proposalMaxOperations, []).then(res => {
        setMaxOperation(Number(res));
      });
    }
  }, [address, instance]);

  useEffect(() => {
    if (
      settings.selectedAddress &&
      (delegateAddress === '' ||
        delegateAddress === '0x0000000000000000000000000000000000000000')
    ) {
      const tokenContract = getTokenContract(instance, 'strk');
      methods
        .call(tokenContract.methods.delegates, [address])
        .then(res => {
          setDelegateAddress(res);
        })
        .catch(() => {});
    }
  }, [settings.selectedAddress, address, delegateAddress, instance]);

  const handleChangePage = (page, size) => {
    setCurrent(page);
    setPageSize(size);
    onChangePage(page, (page - 1) * size, size);
  };

  const onNext = () => {
    handleChangePage(current + 1, 5);
  };

  const onPrev = () => {
    handleChangePage(current - 1, 5);
  };

  const handleShowProposalModal = () => {
    if (+votingWeight < +proposalThreshold) {
      message.error(
        intl.formatMessage(
          {
            id: 'You_can_not_create_proposal'
          },
          { proposalThreshold }
        )
      );
      return;
    }
    const voteContract = getVoteContract(instance);
    setIsLoading(true);
    methods
      .call(voteContract.methods.latestProposalIds, [address])
      .then(pId => {
        if (pId !== '0') {
          methods.call(voteContract.methods.state, [pId]).then(status => {
            if (status === '0' || status === '1') {
              message.error(
                intl.formatMessage({
                  id: 'You_can_not_create_proposal_1'
                })
              );
            } else {
              setProposalModal(true);
            }
            setIsLoading(false);
          });
        } else {
          setProposalModal(true);
          setIsLoading(false);
        }
      });
  };

  return (
    <Card>
      <ProposalsWrapper className="flex flex-column">
        <div className="flex align-center just-between proposal-head">
          <p className="header">
            <FormattedMessage id="Governance_Proposals" />
          </p>
          {address && (
            <Button
              className="create-proposal-btn"
              onClick={handleShowProposalModal}
            >
              {isLoading && <Icon type="loading" />}{' '}
              <FormattedMessage id="Create_Proposal" />
            </Button>
          )}
        </div>
        <div className="body">
          {isLoadingProposal ? (
            <LoadingSpinner />
          ) : proposals && proposals.length !== 0 ? (
            proposals.map(item => {
              return (
                <Proposal
                  proposal={item}
                  votingWeight={votingWeight}
                  delegateAddress={delegateAddress}
                  address={address}
                  key={item.id}
                />
              );
            })
          ) : (
            <NoProposalWrapper className="flex just-center align-center">
              <div className="title">
                <FormattedMessage id="No_Proposals" />
              </div>
            </NoProposalWrapper>
          )}
        </div>
        {proposals && proposals.length !== 0 && (
          <div className="flex align-center just-end footer">
            <Pagination
              defaultCurrent={1}
              defaultPageSize={5}
              current={current}
              onChange={(page, size) => handleChangePage(page, size)}
              total={total}
            />
            {/* <div className="flex just-between align-center button">
              {current * pageSize < total && (
                <div className="flex align-center button-next" onClick={onNext}>
                  <span>Next</span>
                  <img src={arrowRightImg} alt="arrow" />
                </div>
              )}
              {current > 1 && (
                <div className="flex align-center button-prev" onClick={onPrev}>
                  <img src={arrowRightImg} alt="arrow" />
                  <span>Prev</span>
                </div>
              )}
            </div> */}
          </div>
        )}
        {proposalModal && (
          <ProposalModal
            walletConnected={settings.walletConnected}
            address={address}
            visible={proposalModal}
            maxOperation={maxOperation}
            onCancel={() => setProposalModal(false)}
          />
        )}
      </ProposalsWrapper>
    </Card>
  );
}

Proposals.propTypes = {
  address: PropTypes.string.isRequired,
  isLoadingProposal: PropTypes.bool.isRequired,
  votingWeight: PropTypes.number.isRequired,
  settings: PropTypes.object.isRequired,
  proposals: PropTypes.array,
  pageNumber: PropTypes.number,
  total: PropTypes.number,
  onChangePage: PropTypes.func.isRequired,
  intl: intlShape.isRequired
};

Proposals.defaultProps = {
  proposals: [],
  pageNumber: 1,
  total: 0
};

const mapStateToProps = ({ account }) => ({
  settings: account.setting
});

export default injectIntl(
  compose(connectAccount(mapStateToProps, undefined))(Proposals)
);
